import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { multiTemplateGeneratorAPI, categoryAPI } from '../../services/api'
import OccasionScheduler from '../../components/OccasionScheduler/OccasionScheduler'

const MultiTemplateGeneratorFormPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const occasionSchedulerRef = useRef(null)

  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSubCategories, setLoadingSubCategories] = useState(false)
  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [formData, setFormData] = useState({
    group: '',
    template_id: '',
    category_id: '',
    sub_category_id: '',
    is_active: true,
    is_public: false  // Default to private
  })
  const [occasionConfig, setOccasionConfig] = useState({
    isOccasionDateApplicable: false,
    occasionType: 'none',
    occasionStartDate: '',
    occasionEndDate: '',
    occasionStartTime: '00:01',
    occasionEndTime: '23:59',
    isRecurringYearly: false,
    weeklyRecurrenceDays: [],
    monthlyRecurrenceDays: [],
    occasionTimezone: 'Asia/Kolkata',
    occasionMetadata: {}
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    fetchCategories()
    if (isEdit) {
      fetchItem()
    }
  }, [id])

  useEffect(() => {
    if (formData.category_id) {
      fetchSubCategories(formData.category_id)
    } else {
      setSubCategories([])
      setFormData(prev => ({ ...prev, sub_category_id: '' }))
    }
  }, [formData.category_id])

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      const response = await categoryAPI.getAll({ limit: 500 })
      if (response.data.success) {
        const categoriesData = response.data.data?.categories || response.data.data || []
        // Filter for root categories (no parent)
        const rootCategories = categoriesData.filter(cat => !cat.parent_id)
        setCategories(rootCategories)
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchSubCategories = async (parentId) => {
    try {
      setLoadingSubCategories(true)
      const response = await categoryAPI.getSubCategories(parentId)
      if (response.data.success) {
        const subCategoriesData = response.data.data?.categories || response.data.data || []
        setSubCategories(subCategoriesData)
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      setSubCategories([])
    } finally {
      setLoadingSubCategories(false)
    }
  }

  const fetchItem = async () => {
    try {
      setLoading(true)
      const response = await multiTemplateGeneratorAPI.getById(id)
      if (response.data.success) {
        const item = response.data.data
        setFormData({
          group: item.group,
          template_id: item.template_id,
          category_id: item.category_id || '',
          sub_category_id: item.sub_category_id || '',
          is_active: item.is_active,
          is_public: item.is_public || false
        })
        // Set occasion config from item data
        setOccasionConfig({
          isOccasionDateApplicable: item.isOccasionDateApplicable || false,
          occasionType: item.occasionType || 'none',
          occasionStartDate: item.occasionStartDate || '',
          occasionEndDate: item.occasionEndDate || '',
          occasionStartTime: item.occasionStartTime || '00:01',
          occasionEndTime: item.occasionEndTime || '23:59',
          isRecurringYearly: item.isRecurringYearly || false,
          weeklyRecurrenceDays: item.weeklyRecurrenceDays || [],
          monthlyRecurrenceDays: item.monthlyRecurrenceDays || [],
          occasionTimezone: item.occasionTimezone || 'Asia/Kolkata',
          occasionMetadata: item.occasionMetadata || {}
        })
      }
    } catch (error) {
      console.error('Error fetching item:', error)
      alert('Failed to fetch item')
      navigate('/dashboard/multi-template-generator')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.group.trim()) {
      newErrors.group = 'Group is required'
    }

    if (!formData.template_id.trim()) {
      newErrors.template_id = 'Template ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Validate occasion scheduling if enabled
    if (occasionConfig.isOccasionDateApplicable) {
      const isValid = occasionSchedulerRef.current?.validate()
      if (!isValid) {
        alert('Please fix the occasion scheduling configuration errors')
        return
      }
    }

    try {
      setLoading(true)

      // Prepare data - send null for empty strings
      const submitData = {
        ...formData,
        category_id: formData.category_id || null,
        sub_category_id: formData.sub_category_id || null,
        // Include occasion scheduling data
        ...occasionConfig
      }

      if (isEdit) {
        await multiTemplateGeneratorAPI.update(id, submitData)
        alert('Item updated successfully')
      } else {
        await multiTemplateGeneratorAPI.create(submitData)
        alert('Item created successfully')
      }

      navigate('/dashboard/multi-template-generator')
    } catch (error) {
      console.error('Error saving item:', error)
      alert(error.response?.data?.detail || 'Failed to save item')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  if (loading && isEdit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard/multi-template-generator')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? 'Edit' : 'Create'} Multi Template Generator
            </h1>
            <p className="text-gray-600 mt-1">
              {isEdit ? 'Update the template group assignment' : 'Add a new template to a group'}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Group */}
            <div>
              <label htmlFor="group" className="block text-sm font-medium text-gray-700 mb-1">
                Group Name *
              </label>
              <input
                type="text"
                id="group"
                name="group"
                value={formData.group}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.group ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter group name (e.g., 'social-media', 'banners')"
              />
              {errors.group && <p className="text-sm text-red-600 mt-1">{errors.group}</p>}
              <p className="text-sm text-gray-500 mt-1">
                Group name is used to organize templates together
              </p>
            </div>

            {/* Template ID */}
            <div>
              <label htmlFor="template_id" className="block text-sm font-medium text-gray-700 mb-1">
                Template ID *
              </label>
              <input
                type="text"
                id="template_id"
                name="template_id"
                value={formData.template_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.template_id ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter template ID (e.g., 'abc123-def456-...')"
              />
              {errors.template_id && <p className="text-sm text-red-600 mt-1">{errors.template_id}</p>}
              <p className="text-sm text-gray-500 mt-1">
                Enter the UUID of the template you want to add to this group
              </p>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              {loadingCategories ? (
                <div className="text-sm text-gray-500 py-2">Loading categories...</div>
              ) : (
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Optional: Select a category to organize templates
              </p>
            </div>

            {/* Sub Category */}
            <div>
              <label htmlFor="sub_category_id" className="block text-sm font-medium text-gray-700 mb-1">
                Sub Category
              </label>
              {loadingSubCategories ? (
                <div className="text-sm text-gray-500 py-2">Loading subcategories...</div>
              ) : (
                <select
                  id="sub_category_id"
                  name="sub_category_id"
                  value={formData.sub_category_id}
                  onChange={handleInputChange}
                  disabled={!formData.category_id || subCategories.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a subcategory (optional)</option>
                  {subCategories.map(subCategory => (
                    <option key={subCategory.id} value={subCategory.id}>
                      {subCategory.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {!formData.category_id 
                  ? 'Select a category first to see subcategories'
                  : subCategories.length === 0
                  ? 'No subcategories available for selected category'
                  : 'Optional: Select a subcategory for more specific organization'
                }
              </p>
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Active
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Inactive items will not be used in template generation
              </p>
            </div>

            {/* Public Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Public
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Templates created via this generator will be {formData.is_public ? 'public' : 'private'} by default
              </p>
            </div>

            {/* Occasion Scheduling */}
            <div className="pt-4 border-t border-gray-200">
              <OccasionScheduler
                ref={occasionSchedulerRef}
                value={occasionConfig}
                onChange={(config) => setOccasionConfig(config)}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/dashboard/multi-template-generator')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{isEdit ? 'Update' : 'Create'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default MultiTemplateGeneratorFormPage
